import type { MissionDocumentType } from '@backend/lib-document-wrapper-types';
import type { BusinessUnit } from '@backend/lib-l10n';

import type { JobOffer, JobOfferWithJobPost } from '~/business/JobOffer/types';
import { getDomainPublicURL, getJobsUrl } from '~/config';
import { businessUnits } from '~/config/businessUnits';
import { getRegionFromPostalCode } from '~/utils/region';
import { buildTranslate } from '~/utils/translations/buildTranslate';
import { getTranslations } from '~/utils/translations/getTranslations';

const toUtcDateString = (date: string): string => new Date(date).toISOString().slice(0, 'YYYY-MM-DD'.length);

const formatXpRequirementsToJsonLd = (monthsOfExperience: number | undefined) =>
  !monthsOfExperience
    ? 'no requirements'
    : {
        '@type': 'OccupationalExperienceRequirements',
        monthsOfExperience,
      };

// an offer with no certificate required holds the NO_DOCUMENT_REQUIRED type, whose label is not a credential; the labels
// are translated one per type, in the same order
const NO_CERTIFICATE_REQUIRED = 'NO_DOCUMENT_REQUIRED' satisfies MissionDocumentType;

const formatCertificatesToJsonLd = ({ requiredCertificates = [], requiredCertificatesLabels = [] }: JobOffer) => {
  const credentials = requiredCertificatesLabels
    .filter((_label, index) => requiredCertificates[index] !== NO_CERTIFICATE_REQUIRED)
    .map(certificate => ({
      '@type': 'EducationalOccupationalCredential',
      competencyRequired: certificate,
      credentialCategory: 'professional certificate',
    }));
  return credentials.length ? credentials : 'no requirements';
};

const getSalaryUnitText = (unitRate: string) => {
  if (unitRate === 'HOURLY') {
    return 'HOUR';
  }
  if (unitRate === 'MONTHLY') {
    return 'MONTH';
  }
  if (unitRate === 'YEARLY') {
    return 'YEAR';
  }
  return undefined;
};

const getDescriptionRow = (text: string) => `<div><p>${text}</p></div>`;

const getDescription = (jobOffer: JobOffer, businessUnit: BusinessUnit) => {
  const translations = getTranslations(businessUnit);
  const t = buildTranslate(translations, businessUnit);

  const getDescriptionTitle = (titleKey: string) => getDescriptionRow(`${t(`missions.${titleKey}`)} :`);

  const getDescriptionSection = (titleKey: string, value?: string, isHTML = false) =>
    value && `${getDescriptionTitle(titleKey)}${isHTML ? value : getDescriptionRow(value)}`;

  return [
    jobOffer.introductionHTML,
    getDescriptionSection('contractType', jobOffer.typeLabel),
    getDescriptionSection(
      'remuneration',
      jobOffer.salaryFormattedCurrency
        ? `${jobOffer.salaryFormattedCurrency} ${jobOffer.salaryFormattedUnit}`
        : undefined,
    ),
    // an external offer may come without a postal code or without an address
    getDescriptionSection(
      'location',
      [jobOffer.location.postalCode, jobOffer.location.city].filter(Boolean).join(' ') || undefined,
    ),
    getDescriptionSection(
      'workTime',
      jobOffer.weeklyHours ? t('missions.weekly_hours.default', { hours: jobOffer.weeklyHours }) : undefined,
    ),
    getDescriptionSection('about', jobOffer.aboutHTML, true),
    jobOffer.workRatesHTML,
    getDescriptionSection('bonuses', jobOffer.bonusesHTML, true),
    getDescriptionSection('profile', jobOffer.prerequisitesHTML, true),
    getDescriptionSection('certificates', jobOffer.requiredCertificatesLabel),
    getDescriptionSection('experience', jobOffer.requiredExperienceLabel),
    jobOffer.legalInfosHTML,
  ]
    .filter(Boolean)
    .join('');
};

// the employer shown in Google for Jobs: the brand that hires, not the platform serving the page
const getHiringOrganization = (businessUnit: BusinessUnit) => ({
  '@type': 'Organization',
  name: businessUnits[businessUnit].brandName,
  sameAs: getDomainPublicURL(businessUnit),
  // the only logo served is iziwork's: without one, Google takes the brand's from its own site
  ...(!businessUnits[businessUnit].isProman && { logo: `${getJobsUrl()}/images/hiring-company-logo.png` }),
});

/**
 * Generate JSON-LD for the given job offer
 * google job posting documentation : https://developers.google.com/search/docs/appearance/structured-data/job-posting
 * google json-ld format testing : https://search.google.com/test/rich-results
 */
export const getJobOfferJsonld = ({
  jobOffer,
  jobPost,
  businessUnit,
  country,
}: JobOfferWithJobPost & {
  businessUnit: BusinessUnit;
  country: string;
}) => {
  const { minRate, maxRate } = jobOffer.salary;
  const jsonLd: Record<string, any> = {
    // an external offer is applied for on its ATS, embedded in the page behind the apply button
    directApply: !jobOffer.applyLink,
    '@type': 'JobPosting',
    title: jobOffer.title,
    description: getDescription(jobOffer, businessUnit),
    identifier: {
      '@type': 'PropertyValue',
      name: businessUnits[businessUnit].brandName,
      value: jobPost.refId,
    },
    datePosted: toUtcDateString(jobPost.publishedAt),
    employmentType: jobOffer.type === 'PERMANENT' ? 'FULL_TIME' : 'TEMPORARY',
    educationRequirements: formatCertificatesToJsonLd(jobOffer),
    experienceRequirements: formatXpRequirementsToJsonLd(jobOffer.monthsOfExperience),
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressCountry: country,
        addressLocality: jobOffer.location.city,
        addressRegion: jobOffer.location.state || getRegionFromPostalCode(country, jobOffer.location.postalCode),
        postalCode: jobOffer.location.postalCode,
      },
    },
    hiringOrganization: getHiringOrganization(businessUnit),
    // a hidden pay rate reaches here without rates, and an external offer may only have a minimum (its maximum is
    // then 0): a salary of 0 would be shown as such
    ...((minRate || maxRate) && {
      baseSalary: {
        '@type': 'MonetaryAmount',
        currency: jobOffer.salary.currencyCode,
        value: {
          '@type': 'QuantitativeValue',
          ...(minRate && maxRate ? { minValue: minRate, maxValue: maxRate } : { value: maxRate || minRate }),
          unitText: getSalaryUnitText(jobOffer.salary.unitRate),
        },
      },
    }),
  };

  return jsonLd;
};

/**
 * Generate BreadcrumbList JSON-LD: the trail Google shows instead of the URL, the current page last and without a link
 * google breadcrumb documentation : https://developers.google.com/search/docs/appearance/structured-data/breadcrumb
 */
export const getBreadcrumbListJsonld = (items: { name: string; url?: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map(({ name, url }, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name,
    ...(url && { item: url }),
  })),
});
